import { useCallback, useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api, ApiError, newIdempotencyKey } from '../../lib/api';
import { useSession } from '../../lib/useSession';
import { Banner } from '../../components/Banner';
import { Button } from '../../components/Button';
import { Input } from '../../components/Input';
import { errorMessage } from '../members/hooks';
import { clearDraft, loadDraft, saveDraft, type ActivationDraft } from './activationDraft';
import { filterPii, piiWarning } from './pii';
import { parseScannedValue } from './scan';
import { cameraScanSupported } from './camera';
import { Scanner } from './Scanner';
import { domainQuery, PRIMARY_DOMAIN, type ActivatedSlot, type Slot } from './types';
import { BRAND } from '../../brand';

type Step =
  | { kind: 'loading' }
  | { kind: 'not_found' }
  | { kind: 'not_custody' }
  | { kind: 'already_active'; slot: Slot }
  | { kind: 'form' }
  | { kind: 'confirm'; error?: string }
  | { kind: 'sending' }
  | { kind: 'offline' }
  | { kind: 'flagged' }
  | { kind: 'conflict' }
  | { kind: 'done'; slot: ActivatedSlot };

const URL_RE = /^https?:\/\/[^\s/$.?#][^\s]*$/i;

export function ActivatePage() {
  const { code } = useParams();
  return code ? <ActivateCode code={code.toUpperCase()} /> : <ScanStep />;
}

function StepHeader({ back, title }: { back: string; title: string }) {
  return (
    <div className="mb-4 flex items-center gap-3">
      <Link to={back} className="flex min-h-11 min-w-11 items-center justify-center rounded-md text-lg text-ink no-underline hover:bg-gray-50" aria-label="Volver">
        ←
      </Link>
      <h1 className="font-display text-base font-bold text-ink">{title}</h1>
    </div>
  );
}

function Outcome({ tone, icon, title, children, actions }: { tone: 'success' | 'warning' | 'error'; icon: string; title: string; children: ReactNode; actions: ReactNode }) {
  const toneClass = tone === 'success' ? 'bg-green-soft text-success-text' : tone === 'warning' ? 'bg-yellow-soft text-warning-text' : 'bg-white text-error-text border border-error';
  return (
    <div className="flex flex-col items-center gap-4 py-6 text-center" role={tone === 'error' ? 'alert' : 'status'}>
      <span className={`flex h-16 w-16 items-center justify-center rounded-full text-3xl font-bold ${toneClass}`} aria-hidden="true">{icon}</span>
      <h2 className="font-display text-xl font-bold text-ink">{title}</h2>
      <div className="text-sm text-ink-muted">{children}</div>
      <div className="flex w-full flex-col gap-2">{actions}</div>
    </div>
  );
}

function ScanStep() {
  const navigate = useNavigate();
  const [mode, setMode] = useState<'camera' | 'manual'>(() => (cameraScanSupported() ? 'camera' : 'manual'));
  const [cameraProblem, setCameraProblem] = useState<string | null>(() =>
    cameraScanSupported() ? null : 'Este navegador no puede leer QR con la cámara. Escribe el código impreso bajo el QR.',
  );
  const [manual, setManual] = useState('');
  const [error, setError] = useState<string | null>(null);

  const go = useCallback(
    (raw: string) => {
      const parsed = parseScannedValue(raw);
      if (!parsed) {
        setError(`Ese QR no es de ${BRAND}. Escanea un QR de q-r.cl o escribe el código impreso bajo el QR.`);
        return false;
      }
      navigate(`/terreno/activar/${parsed.code}${domainQuery(parsed.domain)}`);
      return true;
    },
    [navigate],
  );

  const onUnavailable = useCallback((reason: 'unsupported' | 'denied' | 'error') => {
    setMode('manual');
    setCameraProblem(
      reason === 'denied'
        ? 'No pudimos usar la cámara (permiso denegado). Escribe el código impreso bajo el QR.'
        : reason === 'unsupported'
          ? 'Este navegador no puede leer QR con la cámara. Escribe el código impreso bajo el QR.'
          : 'No pudimos abrir la cámara. Escribe el código impreso bajo el QR.',
    );
  }, []);

  function onManual(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!manual.trim()) {
      setError('Escribe el código, por ejemplo K7MX.');
      return;
    }
    go(manual);
  }

  return (
    <div>
      <StepHeader back="/terreno" title={`Paso 1 de 3 · ${mode === 'camera' ? 'Escanear' : 'Código'}`} />
      {mode === 'camera' ? (
        <div className="space-y-3">
          <Scanner onDetected={(raw) => { if (!go(raw)) setMode('manual'); }} onUnavailable={onUnavailable} />
          <Button variant="secondary" block size="lg" onClick={() => setMode('manual')}>Escribir el código</Button>
        </div>
      ) : (
        <form onSubmit={onManual} className="space-y-4" noValidate>
          {cameraProblem && <Banner tone="warning">{cameraProblem}</Banner>}
          <Input
            label="Código del QR"
            mono
            autoCapitalize="characters"
            autoComplete="off"
            placeholder="K7MX"
            value={manual}
            onChange={(e) => setManual(e.target.value)}
            className="text-center text-2xl"
            error={error ?? undefined}
          />
          <Button type="submit" block size="lg">Continuar</Button>
          {cameraScanSupported() && (
            <Button variant="ghost" block onClick={() => { setError(null); setCameraProblem(null); setMode('camera'); }}>Usar la cámara</Button>
          )}
        </form>
      )}
      {mode === 'camera' && error && <div className="mt-3"><Banner tone="error">{error}</Banner></div>}
    </div>
  );
}

function ActivateCode({ code }: { code: string }) {
  const [params] = useSearchParams();
  const domain = params.get('domain') || PRIMARY_DOMAIN;
  const { activeOrg, user } = useSession();
  const orgId = activeOrg?.orgId;
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>({ kind: 'loading' });
  const [draft, setDraft] = useState<ActivationDraft>(
    () => loadDraft(code, domain) ?? { code, domain, name: '', externalRef: '', targetUrl: 'https://', idempotencyKey: null },
  );
  const [slot, setSlot] = useState<Slot | null>(null);
  const [touched, setTouched] = useState(false);
  const sendingRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const found = await api.get<Slot>(`/v1/qrs/${encodeURIComponent(code)}${domainQuery(domain)}`, { orgId });
        if (cancelled) return;
        setSlot(found);
        if (found.state === 'activado') setStep({ kind: 'already_active', slot: found });
        else if (found.state !== 'en_custodia' || (activeOrg?.role === 'seller' && found.custodianUid !== user?.uid)) setStep({ kind: 'not_custody' });
        else setStep(loadDraft(code, domain)?.idempotencyKey ? { kind: 'confirm' } : { kind: 'form' });
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && (err.kind === 'not_found' || err.kind === 'forbidden')) setStep({ kind: 'not_found' });
        else if (err instanceof ApiError && err.kind === 'network') setStep({ kind: 'form' });
        else setStep({ kind: 'not_found' });
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [code, domain, orgId, activeOrg?.role, user?.uid]);

  const update = (patch: Partial<ActivationDraft>) => {
    // Cambiar los datos invalida la clave: una activación distinta no debe reutilizar la anterior.
    const next = { ...draft, ...patch, idempotencyKey: null };
    setDraft(next);
    saveDraft(next);
  };

  const namePii = filterPii(draft.name);
  const refPii = filterPii(draft.externalRef);
  const piiKinds = [...new Set([...namePii.kinds, ...refPii.kinds])];
  const errors = {
    name: !draft.name.trim() ? 'Escribe el nombre del negocio.' : draft.name.length > 120 ? 'Máximo 120 caracteres.' : undefined,
    externalRef: draft.externalRef.length > 128 ? 'Máximo 128 caracteres.' : undefined,
    targetUrl: !URL_RE.test(draft.targetUrl.trim()) ? 'El destino debe empezar con https:// o http://.' : undefined,
  };
  const valid = !errors.name && !errors.externalRef && !errors.targetUrl;

  function toConfirm(e: FormEvent) {
    e.preventDefault();
    setTouched(true);
    if (!valid) return;
    const next = { ...draft, idempotencyKey: draft.idempotencyKey ?? newIdempotencyKey() };
    setDraft(next);
    saveDraft(next);
    setStep({ kind: 'confirm' });
  }

  const send = useCallback(async () => {
    if (sendingRef.current) return;
    const key = draft.idempotencyKey ?? newIdempotencyKey();
    const current = { ...draft, idempotencyKey: key };
    saveDraft(current);
    setDraft(current);
    sendingRef.current = true;
    setStep({ kind: 'sending' });
    try {
      const result = await api.post<ActivatedSlot>(
        `/v1/qrs/${encodeURIComponent(code)}/activate${domainQuery(domain)}`,
        { business: { name: current.name.trim(), externalRef: current.externalRef.trim() || null }, targetUrl: current.targetUrl.trim() },
        { idempotencyKey: key, orgId },
      );
      clearDraft();
      setStep({ kind: 'done', slot: result });
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.kind === 'network') setStep({ kind: 'offline' });
        else if (err.code === 'already_activated') {
          clearDraft();
          setStep({ kind: 'conflict' });
        } else if (err.code === 'destination_flagged') setStep({ kind: 'flagged' });
        else if (err.kind === 'not_found') setStep({ kind: 'not_custody' });
        else if (err.kind === 'validation') {
          setTouched(true);
          setStep({ kind: 'confirm', error: err.message });
        } else setStep({ kind: 'confirm', error: errorMessage(err) });
      } else {
        setStep({ kind: 'confirm', error: errorMessage(err) });
      }
    } finally {
      sendingRef.current = false;
    }
  }, [code, domain, draft, orgId]);

  // Sin señal: al volver la conexión se reintenta solo, con la misma clave de idempotencia.
  useEffect(() => {
    if (step.kind !== 'offline') return;
    const onOnline = () => void send();
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, [step.kind, send]);

  const lot = slot?.lotId ? `${slot.lotId} · ` : '';

  switch (step.kind) {
    case 'loading':
      return <p className="p-4 text-sm text-ink-muted" role="status">Buscando el QR {code}…</p>;
    case 'not_found':
      return (
        <Outcome tone="error" icon="!" title="No encontramos ese QR" actions={<Link to="/terreno/activar" className="text-center">Escanear otro</Link>}>
          Revisa el código <span className="font-mono">{code}</span>. Si es un QR de otra organización, no aparece aquí.
        </Outcome>
      );
    case 'not_custody':
      return (
        <Outcome tone="warning" icon="!" title="Este QR no está en tu custodia" actions={<Link to="/terreno/activar" className="text-center">Escanear otro</Link>}>
          Pídele a tu Admin que te lo asigne.
        </Outcome>
      );
    case 'already_active':
      return (
        <Outcome tone="warning" icon="!" title="Este QR ya está activado" actions={<Link to="/terreno" className="text-center">Volver al inicio</Link>}>
          <span className="font-mono">{code}</span> ya lleva a {step.slot.targetUrl ?? 'su destino'}.
        </Outcome>
      );
    case 'conflict':
      return (
        <Outcome tone="warning" icon="!" title="Este QR ya fue activado" actions={<Link to="/terreno" className="text-center">Volver al inicio</Link>}>
          Otra activación llegó primero. No se aplicaron tus datos.
        </Outcome>
      );
    case 'flagged':
      return (
        <Outcome tone="error" icon="✕" title="Este enlace está marcado como peligroso" actions={<Button block size="lg" onClick={() => setStep({ kind: 'form' })}>Cambiar destino</Button>}>
          No podemos usarlo como destino. El intento quedó registrado.
        </Outcome>
      );
    case 'done': {
      const shortUrl = step.slot.shortUrl ?? `https://${domain}/${code}`;
      return (
        <Outcome
          tone="success"
          icon="✓"
          title="QR activado"
          actions={
            <>
              <Button block size="lg" onClick={() => navigate('/terreno/activar')}>Activar otro</Button>
              <Link to="/terreno" className="text-center">Volver al inicio</Link>
            </>
          }
        >
          <p>
            <a href={shortUrl} className="font-mono">{shortUrl.replace(/^https?:\/\//, '')}</a> ya lleva a {step.slot.targetUrl}. Pruébalo escaneándolo con otro celular.
          </p>
          {step.slot.destStatus === 'pending' && (
            <p className="mt-2">No pudimos revisar el enlace en este momento. El QR ya funciona y lo revisaremos en las próximas horas.</p>
          )}
          {step.slot.piiNotice && <p className="mt-2">{step.slot.piiNotice.message}.</p>}
        </Outcome>
      );
    }
    case 'offline':
      return (
        <div>
          <StepHeader back="/terreno" title="Paso 3 de 3" />
          <div className="space-y-4">
            <Banner tone="warning">Sin conexión. Guardamos lo que ingresaste; lo enviaremos cuando vuelva la señal.</Banner>
            <Button block size="lg" onClick={() => void send()}>Reintentar</Button>
          </div>
        </div>
      );
    case 'confirm':
    case 'sending':
      return (
        <div>
          <StepHeader back="/terreno" title="Paso 3 de 3 · Confirmar" />
          <div className="space-y-4">
            {step.kind === 'confirm' && step.error && <Banner tone="error">{step.error}</Banner>}
            <dl className="divide-y divide-line rounded-lg border border-line bg-white text-sm">
              {[
                ['Código', <span key="c" className="font-mono">{code}</span>],
                ['Negocio', namePii.text],
                ['Referencia', refPii.text || '—'],
                ['Destino', <span key="d" className="break-all">{draft.targetUrl}</span>],
              ].map(([label, value]) => (
                <div key={String(label)} className="flex justify-between gap-4 px-4 py-3">
                  <dt className="text-ink-muted">{label}</dt>
                  <dd className="text-right text-ink">{value}</dd>
                </div>
              ))}
            </dl>
            <Button block size="lg" loading={step.kind === 'sending'} onClick={() => void send()}>Activar QR</Button>
            <Button variant="secondary" block onClick={() => setStep({ kind: 'form' })} disabled={step.kind === 'sending'}>Corregir datos</Button>
          </div>
        </div>
      );
    case 'form':
    default:
      return (
        <div>
          <StepHeader back="/terreno/activar" title="Paso 2 de 3 · Negocio y destino" />
          <form onSubmit={toConfirm} className="space-y-4" noValidate>
            <p className="rounded-md bg-mist px-3 py-2 text-sm">
              <span className="font-mono font-semibold">{code}</span> <span className="text-ink-muted">{lot}en tu custodia</span>
            </p>
            <Input label="Nombre del negocio" value={draft.name} maxLength={120} onChange={(e) => update({ name: e.target.value })} error={touched ? errors.name : undefined} />
            <Input
              label={`Referencia en ${activeOrg?.orgName ?? 'tu sistema'} (opcional)`}
              value={draft.externalRef}
              maxLength={128}
              onChange={(e) => update({ externalRef: e.target.value })}
              error={touched ? errors.externalRef : undefined}
            />
            {piiKinds.length > 0 && (
              <Banner tone="warning">
                {piiWarning(piiKinds)} Se guardará como “{namePii.text}”.
              </Banner>
            )}
            <Input
              label="Destino"
              type="url"
              inputMode="url"
              autoCapitalize="none"
              value={draft.targetUrl}
              onChange={(e) => update({ targetUrl: e.target.value })}
              error={touched ? errors.targetUrl : undefined}
            />
            <p className="text-xs text-ink-muted">No se piden RUT, email ni teléfono del negocio.</p>
            <Button type="submit" block size="lg">Revisar</Button>
          </form>
        </div>
      );
  }
}
